// ─── Constants ────────────────────────────────────────────────────────────────

export const PAGE_SIZE = 20;
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

export const TAB = { HEALTH: 'health', CALLS: 'calls', RUNS: 'runs' };

export const DEFAULT_FILTERS = {
  from: '',
  to: '',
  service: 'all',
  feature: 'all',
  environment: 'all',
  outcome: 'all',
  job: 'all',
  subjectId: '',
  runId: ''
};

// ─── Formatters ───────────────────────────────────────────────────────────────

export const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'UTC',
    timeZoneName: 'short'
  });
};

export const fmtDuration = (ms) => {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  const mins = Math.floor(ms / 60_000);
  return `${mins}m ${Math.round((ms % 60_000) / 1000)}s`;
};

/** "4h 12m ago", or a dash. Used for time-since-last-success, which is read at a glance. */
export const fmtSince = (ms) => {
  if (ms == null) return 'never';
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ${mins % 60}m ago`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h ago`;
};

export const fmtPercent = (rate) =>
  rate == null || Number.isNaN(rate) ? '—' : `${(rate * 100).toFixed(1)}%`;

export const fmtCount = (n) => (n == null ? '—' : Number(n).toLocaleString('en-GB'));

// ─── Badges ───────────────────────────────────────────────────────────────────

const BADGE_BASE = 'px-2 py-[2px] rounded-md text-[11px] font-semibold whitespace-nowrap';

export const badge = (tone) => {
  const map = {
    good: 'bg-[#F0FDF4] text-[#16A34A] border border-[#BBF7D0]',
    warn: 'bg-[#FFF7ED] text-[#EA580C] border border-[#FED7AA]',
    bad: 'bg-[#FFF1F2] text-[#E11D48] border border-[#FECDD3]',
    info: 'bg-[#EFF6FF] text-[#2563EB] border border-[#BFDBFE]',
    muted: 'bg-[#F1F5F9] text-[#64748B] border border-[#E2E8F0]'
  };
  return `${BADGE_BASE} ${map[tone] || map.muted}`;
};

export const callOutcomeTone = (outcome) => (outcome === 'success' ? 'good' : 'bad');

export const runOutcomeTone = (outcome) => {
  const map = {
    succeeded: 'good',
    failed: 'bad',
    in_progress: 'info',
    indeterminate: 'warn'
  };
  return map[outcome] || 'muted';
};

export const httpCodeColor = (code) => {
  if (!code) return 'text-[#94A3B8]';
  if (code >= 500) return 'text-[#E11D48]';
  if (code >= 400) return 'text-[#EA580C]';
  if (code >= 200 && code < 300) return 'text-[#16A34A]';
  return 'text-[#64748B]';
};

// ─── Query building ───────────────────────────────────────────────────────────

/**
 * Turn filter state into query params.
 *
 * Dates are widened to whole UTC days: a date input gives `YYYY-MM-DD`, and an admin picking a
 * single day means all of it, not midnight.
 */
export const toParams = (filters, extra = {}) => {
  const params = new URLSearchParams();
  if (filters.from) params.append('from', `${filters.from}T00:00:00.000Z`);
  if (filters.to) params.append('to', `${filters.to}T23:59:59.999Z`);
  ['service', 'feature', 'environment', 'outcome', 'job'].forEach((key) => {
    if (filters[key] && filters[key] !== 'all') params.append(key, filters[key]);
  });
  if (filters.subjectId) params.append('subjectId', filters.subjectId);
  if (filters.runId) params.append('runId', filters.runId);
  Object.entries(extra).forEach(([k, v]) => {
    if (v != null && v !== '') params.append(k, v);
  });
  return params;
};

/**
 * Ask the server for the CSV rather than building one here.
 *
 * The backend export is the single definition of what an export contains — it applies the same
 * allow-list projection the tables use and the same 10,000-row cap. Rebuilding it client-side from
 * the current page would silently export one page and omit the environment column that makes
 * exports from several environments combinable.
 */
export const buildExportUrl = (kind, filters) =>
  `/admin/activity/${kind}Export?${toParams(filters).toString()}`;
